import { useState, useEffect, useCallback } from "react";
import {
  EntityTypeEnum,
  RegtankOnboardingFHStatusEnum,
  RegtankLivenessFHStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import {
  processOnboardingData,
  processLivenessData,
  resultOnboardingChecking,
  resultLivenessChecking,
  rawOnboardingData,
  rawLivenessData,
} from "../../../../Utils/getRegtankDetails";
import type {
  RegtankData,
  RegtankLivenessData,
  RegtankHistoryData,
  RegtankLivenessHistoryData,
} from "../Regtank.types";
import type { Data, UserData } from "../../../../types/org.type";
import { convertToUtcTwelveHours } from "@funded-here-interface/common/src/Utils/formatter";
import {
  sortRegtankRequestsByExpiry,
  toISOString,
} from "@funded-here-interface/common/src/Utils/regtankOnboardingHelper";

interface UseRegtankDataProps {
  userData: UserData[] | undefined;
  orgData: Data | undefined;
  updatedFinalData: RegtankData[];
  updatedLivenessFinalData: RegtankLivenessData[];
}

/**
 * Calculates the number of attempt used based on the submitted date
 */

const calculateNumberOfAttemptUsed = (
  sortedData: any[],
  usableIndices: any[]
): string => {
  if (sortedData.length > 0 && usableIndices.length > 0) {
    let numberOfDataLivenessDone = sortedData
      .filter((x) => x.submittedOn)
      .length.toString();
    if (numberOfDataLivenessDone === "0") {
      numberOfDataLivenessDone = "";
    }
    return numberOfDataLivenessDone.toString();
  }
  return "";
};

/**
 * Calculates the updated attempt date based on sorted data and result
 */
const calculateAttemptDate = (
  sortedData: any[],
  newSubmittedOn: string
): string => {
  if (sortedData.length > 1) {
    const lastSubmittedDate = sortedData[sortedData.length - 1].submittedDate;
    return newSubmittedOn === "" && lastSubmittedDate
      ? convertToUtcTwelveHours(lastSubmittedDate)
      : convertToUtcTwelveHours(newSubmittedOn);
  }
  return newSubmittedOn !== "" ? convertToUtcTwelveHours(newSubmittedOn) : "";
};

/**
 * Custom hook to handle all data transformation and processing
 * Consolidates the massive useEffect logic into a single, manageable hook
 */
export const useRegtankData = ({
  userData,
  orgData,
  updatedFinalData,
  updatedLivenessFinalData,
}: UseRegtankDataProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [finalData, setFinalData] = useState<RegtankData[]>([]);
  const [livenessFinalData, setLivenessFinalData] = useState<
    RegtankLivenessData[]
  >([]);
  const [regtankFHResultTotal, setRegtankFHResultTotal] = useState<
    resultOnboardingChecking[]
  >([]);
  const [livenessFHResultTotal, setLivenessFHResultTotal] = useState<
    resultLivenessChecking[]
  >([]);
  const [verificationHistoryData, setVerificationHistoryData] = useState<
    RegtankHistoryData[]
  >([]);
  const [livenessHistoryData, setLivenessHistoryData] = useState<
    RegtankLivenessHistoryData[]
  >([]);
  const [regtankOnboardingPerUserRowData, setRegtankOnboardingPerUserRowData] =
    useState<RegtankData>();
  const [regtankLivenessCardData, setRegtankLivenessCardData] =
    useState<RegtankLivenessData>();

  const getUserType = useCallback(
    (data: UserData): string => {
      let userType = "";
      if (
        data?.orgRole?.[0].role?.name === "investor" &&
        data?.entityType === EntityTypeEnum.INDIVIDUAL
      ) {
        userType = "Individual Investor";
      } else {
        const isDirector = orgData?.boardOfDirectors.find(
          (x) => x.email === data.email
        );
        const isSuspended = data.isSuspended;
        const isAuthRep =
          orgData?.emailOfAuthorisedRepresentiative === data.email;
        const authRepIsAdmin = orgData?.authRepIsMerchant;
        if (isDirector && isAuthRep) {
          userType = "Director & Auth Rep";
        } else if (isAuthRep && authRepIsAdmin) {
          userType = "Admin & Auth Rep";
        } else if (isDirector) {
          userType = "Director";
        } else if (isAuthRep) {
          userType = "Auth Rep";
        } else if (isSuspended) {
          userType = "Removed User";
        }
      }
      return userType;
    },
    [orgData]
  );

  /**
   * Generic data processor for both onboarding and liveness data
   */
  const processRegtankData = useCallback(
    <TData, TResult>(config: {
      rawDataKey: "regtankRequest" | "regtankLiveness";
      processResult: (sortedData: any[]) => TResult[];
      buildTableData: (
        user: UserData,
        result: TResult,
        attemptDate: string,
        usableIndices: number[],
        attemptUsed: string
      ) => TData;
      buildEmptyTableData: (user: UserData) => TData;
      shouldProcess: (user: UserData) => boolean;
    }): { tableData: TData[]; results: TResult[] } => {
      const tableData: TData[] = [];
      const results: TResult[] = [];

      if (!userData || userData.length === 0) {
        return { tableData, results };
      }

      for (const user of userData) {
        if (user?.regtankOnboarding) {
          const rawData = user[config.rawDataKey];
          if (rawData && rawData.length > 0) {
            let onboardingData: rawOnboardingData[] = [];
            let livenessData: rawLivenessData[] = [];
            const sortedData = sortRegtankRequestsByExpiry(rawData);
            //construct the sortedData to become the parameter for processResult
            if (config.rawDataKey === "regtankRequest") {
              for (const data of sortedData) {
                onboardingData.push({
                  requestId: data.requestId,
                  onboardingStatus: data.onboardingStatus,
                  expiredDate: data.expiredDate,
                  reattemptApproval: data.reattemptApproval,
                  submittedOn: data.submittedDate,
                  reviewedOn: data.reviewedDate,
                  remark: data.remark,
                  reason: data.reason,
                  documentIdSubmittedDate: data.documentIdSubmittedDate,
                });
              }
            } else if (config.rawDataKey === "regtankLiveness") {
              for (const data of sortedData) {
                livenessData.push({
                  sessionId: data.sessionId,
                  livenessStatus: data.livenessStatus,
                  expiredDate: toISOString(data.expiredDate),
                  numberOfLiveness: sortedData.length - 1,
                  reattemptApproval: data.reattemptApproval,
                  submittedOn: toISOString(data.submittedDate),
                  failureReason: data.reason,
                });
              }
            }
            let result =
              config.rawDataKey === "regtankLiveness"
                ? config.processResult(livenessData)
                : config.processResult(onboardingData);

            // Find usable (non-hidden) indices - simplified logic
            const usableIndices = result.reduce<number[]>((indices, item, index) => {
              const isHidden = (item as any).hiddenVerificationHistoryData ||
                               (item as any).hiddenLivenessHistoryData;
              if (!isHidden) {
                indices.push(index);
              }
              return indices;
            }, []);

            // Use correct data array for calculations based on data type
            const dataForCalculation = config.rawDataKey === "regtankRequest"
              ? onboardingData
              : livenessData;

            const attemptDate = calculateAttemptDate(
              dataForCalculation,
              (result?.[usableIndices[0]] as any)?.newSubmittedOn
            );
            const attemptUsed = calculateNumberOfAttemptUsed(
              dataForCalculation,
              usableIndices
            );
            const data = config.buildTableData(
              user,
              result?.[usableIndices[0]],
              attemptDate,
              usableIndices,
              attemptUsed
            );
            results.push(...result);

            tableData.push(data);
          } else if (config.shouldProcess(user)) {
            const data = config.buildEmptyTableData(user);
            tableData.push(data);
          }
        }
      }
      tableData.sort((a: TData, b: TData) => {
        const userRoleA = (a as any).userRole.toUpperCase();
        const userRoleB = (b as any).userRole.toUpperCase();
        if (userRoleA < userRoleB) {
          return -1;
        } else if (userRoleA > userRoleB) {
          return 1;
        }
        return 0;
      });

      return { tableData, results };
    },
    [userData]
  );

  // Process onboarding data
  useEffect(() => {
    if (!userData || userData.length === 0 || !userData[0].regtankRequest) {
      setFinalData([]);
      return;
    }

    const { tableData, results } = processRegtankData<
      RegtankData,
      resultOnboardingChecking
    >({
      rawDataKey: "regtankRequest",
      processResult: (sortedData: any[]) => processOnboardingData(sortedData),
      buildTableData: (
        user,
        result,
        attemptDate,
        usableIndices,
        attemptUsed
      ) => ({
        name: user.firstName + " " + user.lastName,
        email: user.email,
        userRole: getUserType(user),
        onboardingStatus:
          usableIndices.length > 0
            ? result?.newStatus
            : RegtankOnboardingFHStatusEnum.PENDING_VERIFICATION,
        lastAttemptDate: attemptDate,
        attemptUsed: attemptUsed,
        userId: user.id,
      }),
      buildEmptyTableData: (user) => ({
        name: user.firstName + " " + user.lastName,
        email: user.email,
        userRole: getUserType(user),
        onboardingStatus: RegtankOnboardingFHStatusEnum.PENDING_VERIFICATION,
        lastAttemptDate: " ",
        attemptUsed: "",
        userId: user.id,
      }),
      shouldProcess: () => true,
    });
    setRegtankFHResultTotal(results);

    // Apply updates if available
    if (updatedFinalData.length > 0 && finalData.length > 0) {
      const updatedMap = new Map<number, RegtankData>(
        updatedFinalData.map((x) => [x.userId, x])
      );
      setFinalData((prev) =>
        prev.map((item) => {
          const updated = updatedMap.get(item.userId);
          return updated ? { ...item, ...updated } : item;
        })
      );
    } else {
      setFinalData(tableData);
    }
  }, [userData, updatedFinalData, getUserType, processRegtankData]);

  // Process liveness data
  useEffect(() => {
    if (!userData || userData.length === 0) {
      setLivenessFinalData([]);
      setIsLoading(false);
      return;
    }

    const { tableData, results } = processRegtankData<
      RegtankLivenessData,
      resultLivenessChecking
    >({
      rawDataKey: "regtankLiveness",
      processResult: (sortedData) => processLivenessData(sortedData),
      buildTableData: (user, result, attemptDate, usableIndices) => ({
        name: user.firstName + " " + user.lastName,
        email: user.email,
        userRole: getUserType(user),
        verificationStatus:
          usableIndices.length > 0
            ? result?.newStatus
            : RegtankLivenessFHStatusEnum.NOTHING,
        lastAttemptDate: attemptDate,
        userId: user.id,
      }),
      buildEmptyTableData: (user) => ({
        name: user.firstName + " " + user.lastName,
        email: user.email,
        userRole: getUserType(user),
        verificationStatus: RegtankLivenessFHStatusEnum.NOTHING,
        lastAttemptDate: " ",
        userId: user.id,
      }),
      shouldProcess: () => true,
    });

    setLivenessFHResultTotal(results);

    // Apply updates if available
    if (updatedLivenessFinalData.length > 0 && livenessFinalData.length > 0) {
      const updatedMap = new Map<number, RegtankLivenessData>(
        updatedLivenessFinalData.map((x) => [x.userId, x])
      );
      setLivenessFinalData((prev) =>
        prev.map((item) => {
          const updated = updatedMap.get(item.userId);
          return updated ? { ...item, ...updated } : item;
        })
      );
    } else {
      setLivenessFinalData(tableData);
    }

    setIsLoading(false);
  }, [userData, updatedLivenessFinalData, getUserType, processRegtankData]);

  return {
    isLoading,
    finalData,
    setFinalData,
    livenessFinalData,
    setLivenessFinalData,
    regtankFHResultTotal,
    livenessFHResultTotal,
    verificationHistoryData,
    setVerificationHistoryData,
    livenessHistoryData,
    setLivenessHistoryData,
    regtankOnboardingPerUserRowData,
    setRegtankOnboardingPerUserRowData,
    regtankLivenessCardData,
    setRegtankLivenessCardData,
    convertToUtcTwelveHours,
    getUserType,
  };
};
